import { useState } from 'react'

import { Box, Drawer, Fade, Snackbar } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ErrorBoundary } from '@scoir/error-reporter'

import ErrorMessage from 'components/ErrorMessage'

import { ToggleTargeted } from '../ExploreTable/useTargetHighschoolsExplore'
import { SchoolType, Title1Program } from '../types'
import DemographicsDetails from './DemographicsDetails'
import LocationDetails from './LocationDetails'
import PostSecondaryEnrollmentDetails from './PostSecondaryEnrollmentDetails'
import PreviewDrawerNav from './PreviewDrawerNav'
import PreviewHeader from './PreviewHeader'
import SchoolDetails from './SchoolDetails'
import StudentEngagementDetails from './StudentEngagementDetails'
import VisitsDetails from './VisitsDetails'
import useGetHighschoolPreviewData from './useGetHighschoolPreviewData'

const useStyles = makeStyles((theme) => ({
  drawerRoot: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'inherit',
    },
  },
  drawerPaperClass: {
    transition: 'smooth',
    overflowY: 'scroll',
    width: 550,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  header: {
    backgroundColor: theme.palette.common.white,
    position: 'sticky',
    top: 0,
    zIndex: 3,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}))

export type ExplorePreviewRecord = {
  id: string
  name: string
  city: string
  state: string
  schoolType?: SchoolType | undefined
  seniorClassSize?: number | undefined
  nextVisitDate?: string | undefined
  title1Program?: Title1Program | undefined
  pctFreeReducedLunch?: number | undefined
  active?: boolean
}
interface HighschoolPreviewDrawer {
  open: boolean
  onClose: () => void
  highschool?: ExplorePreviewRecord | null
  onClickNext: (highschoolId?: string) => void
  hasNext: boolean
  onClickPrev: (highschoolId?: string) => void
  hasPrevious: boolean
  dataIsLoading: boolean
  toggleTargeted: ToggleTargeted
}

const HighschoolPreviewDrawer = ({
  open,
  dataIsLoading,
  onClose,
  highschool,
  onClickNext,
  hasNext,
  onClickPrev,
  hasPrevious,
  toggleTargeted,
}: HighschoolPreviewDrawer) => {
  const classes = useStyles()
  const [innerModalOpen, setInnerModalOpen] = useState(false)
  const highschoolResponse = useGetHighschoolPreviewData({ highschoolId: highschool?.id, enabled: open })

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      className={classes.drawerRoot}
      ModalProps={{
        disableEnforceFocus: innerModalOpen,
      }}
      PaperProps={{ elevation: 6, className: classes.drawerPaperClass }}
    >
      <ErrorBoundary errorComponent={() => <ErrorMessage />}>
        <Box>
          <Box className={classes.header}>
            <PreviewDrawerNav
              navDisabled={dataIsLoading}
              onClose={onClose}
              highschool={highschool}
              onClickNext={onClickNext}
              hasNext={hasNext}
              onClickPrev={onClickPrev}
              hasPrevious={hasPrevious}
            />
            {highschoolResponse.data?.highschool && !highschoolResponse.isLoading && (
              <Fade
                key={highschoolResponse.data.highschool.id}
                in={true}
                timeout={600}
              >
                <PreviewHeader
                  highschool={highschoolResponse.data?.highschool}
                  setInnerModalOpen={setInnerModalOpen}
                  toggleTargeted={toggleTargeted}
                />
              </Fade>
            )}
          </Box>

          {highschoolResponse.data?.highschool && !highschoolResponse.isLoading && (
            <Fade
              key={highschoolResponse.data.highschool.id}
              in={true}
              timeout={600}
            >
              <Box
                display="flex"
                flexDirection="column"
                m={3}
                pb={3}
                gridGap={40}
              >
                <SchoolDetails highschool={highschoolResponse.data.highschool} />
                <LocationDetails highschool={highschoolResponse.data.highschool} />
                {highschoolResponse.data.highschool.isActive && (
                  <VisitsDetails highschool={highschoolResponse.data.highschool} />
                )}
                <DemographicsDetails highschool={highschoolResponse.data.highschool} />
                {highschoolResponse.data.highschool.isActive && (
                  <StudentEngagementDetails
                    studentEngagement={highschoolResponse.data.studentEngagement || []}
                    currentSeniorYear={highschoolResponse.data.currentSeniorYear || new Date().getFullYear()}
                  />
                )}
                <PostSecondaryEnrollmentDetails highschool={highschoolResponse.data.highschool} />
              </Box>
            </Fade>
          )}
          <Snackbar
            open={highschoolResponse.isError}
            message="There was an error getting the high school."
          />
        </Box>
      </ErrorBoundary>
    </Drawer>
  )
}

export default HighschoolPreviewDrawer
